/**
 * Document lifecycle.
 *
 *   DRAFT ──issue──▶ ISSUED ──allocate──▶ PARTIALLY_SETTLED ──▶ SETTLED
 *     │                 │
 *     │                 ├── cancel ──▶ CANCELLED   (reverses stock, voids allocations)
 *     │                 │
 *     │                 └── returned ─▶ RETURNED   (balance 0, reversal posted)
 *     │
 *     └── delete ──▶ gone
 *
 * Implemented as pure guards returning a result object, so the same rules
 * drive the UI (disable the button, show why) and the service (reject the
 * write). A rule that exists only in the UI is a rule that gets bypassed.
 */
import type { DocType } from './docTypes';
import { docSpec } from './docTypes';

export const DOC_STATUSES = [
  'DRAFT',
  'ISSUED',
  'PARTIALLY_SETTLED',
  'SETTLED',
  'CANCELLED',
  'RETURNED',
] as const;

export type DocStatus = (typeof DOC_STATUSES)[number];

export type GuardCode =
  | 'EMPTY_DOCUMENT'
  | 'NO_LINES'
  | 'MISSING_PARTY'
  | 'INVALID_STATE'
  | 'ALREADY_ISSUED'
  | 'ALREADY_CANCELLED'
  | 'HAS_PAYMENTS'
  | 'IS_REFERENCED'
  | 'NOT_ISSUED'
  | 'NOT_CORRECTABLE_TYPE'
  | 'RETURN_EXCEEDS_INVOICED';

export type GuardResult = { ok: true } | { ok: false; code: GuardCode; message: string };

export interface DocumentFacts {
  docType: DocType;
  status: DocStatus;
  lineCount: number;
  hasCounterparty: boolean;
  /** Paise allocated to this document by payments. */
  allocatedPaise: number;
  /** True when another document links to this one. */
  isReferenced: boolean;
}

const ok: GuardResult = { ok: true };
const fail = (code: GuardCode, message: string): GuardResult => ({ ok: false, code, message });

/** A draft becomes an issued document. */
export function canIssue(doc: DocumentFacts): GuardResult {
  if (doc.status !== 'DRAFT') {
    return doc.status === 'ISSUED'
      ? fail('ALREADY_ISSUED', 'This document has already been issued.')
      : fail('INVALID_STATE', `A ${doc.status.toLowerCase()} document cannot be issued.`);
  }
  if (doc.lineCount === 0) return fail('NO_LINES', 'Add at least one line before issuing.');
  if (!doc.hasCounterparty) {
    return fail('MISSING_PARTY', `Select a ${docSpec(doc.docType).side === 'SALES' ? 'customer' : 'supplier'} before issuing.`);
  }
  return ok;
}

/**
 * An issued document is corrected, never edited.
 *
 * This is both an accounting control and a legal requirement: a GST invoice
 * that has been reported cannot be silently changed.
 */
export function canEdit(doc: DocumentFacts, allowEditIssued: boolean): GuardResult {
  if (doc.status === 'DRAFT') return ok;
  if (doc.status === 'CANCELLED') {
    return fail('ALREADY_CANCELLED', 'A cancelled document cannot be edited.');
  }
  if (allowEditIssued) return ok;
  return fail(
    'INVALID_STATE',
    'An issued document cannot be edited. Raise a credit note instead.',
  );
}

/**
 * Cancelling requires the payments to be reversed first.
 *
 * Voiding a payment silently would leave the party's ledger disagreeing with
 * the bank, so the service refuses and the user reverses explicitly.
 */
export function canCancel(doc: DocumentFacts): GuardResult {
  if (doc.status === 'DRAFT') return ok;
  if (doc.status === 'CANCELLED') {
    return fail('ALREADY_CANCELLED', 'This document is already cancelled.');
  }
  if (doc.allocatedPaise > 0) {
    return fail(
      'HAS_PAYMENTS',
      'Reverse the payments recorded against this document before cancelling it.',
    );
  }
  return ok;
}

/** Only a document nothing depends on may be deleted outright. */
export function canDelete(doc: DocumentFacts): GuardResult {
  if (doc.isReferenced) {
    return fail('IS_REFERENCED', 'Another document depends on this one. Cancel it instead.');
  }
  if (doc.status !== 'DRAFT' && doc.status !== 'CANCELLED') {
    return fail('INVALID_STATE', 'An issued document must be cancelled, not deleted.');
  }
  return ok;
}

/** Returns and credit/debit notes apply to an issued document of the right type. */
export function canCorrect(
  doc: DocumentFacts,
  correctionType: DocType,
): GuardResult {
  if (doc.status === 'DRAFT' || doc.status === 'CANCELLED') {
    return fail('NOT_ISSUED', 'Only an issued document can be corrected.');
  }
  const spec = docSpec(correctionType);
  if (spec.corrects !== doc.docType) {
    return fail(
      'NOT_CORRECTABLE_TYPE',
      `A ${spec.label} corrects a ${docSpec(doc.docType).label}, not this document.`,
    );
  }
  return ok;
}

/** The states a document may be in for a given operation, for the UI. */
export function allowedTransitions(status: DocStatus): readonly DocStatus[] {
  switch (status) {
    case 'DRAFT':
      return ['ISSUED', 'CANCELLED'];
    case 'ISSUED':
      return ['PARTIALLY_SETTLED', 'SETTLED', 'RETURNED', 'CANCELLED'];
    case 'PARTIALLY_SETTLED':
      return ['SETTLED', 'CANCELLED'];
    case 'SETTLED':
      return ['RETURNED'];
    case 'CANCELLED':
    case 'RETURNED':
      return [];
    default:
      return [];
  }
}

/** Status implied by the allocated amount, used when a payment is written. */
export function derivePaymentStatus(grandTotalPaise: number, allocatedPaise: number): DocStatus {
  if (allocatedPaise <= 0) return 'ISSUED';
  if (allocatedPaise >= grandTotalPaise) return 'SETTLED';
  return 'PARTIALLY_SETTLED';
}
